import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { TextField, IconButton } from '@mui/material';
import { manualApi } from '../../api/global';
import Autocomplete from '@mui/material/Autocomplete';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import ArrowIc from '../../assets/images/ic_arrow_line.svg';
import SearchIc from '../../assets/images/ic_search.svg';

const DefaultValue = 'default';

function FilterSearch({ location, setManuals, reload }) {
  const [parnetCategories, setParentCategories] = useState([]);
  const [selectedParentCategory, setParentCategory] = useState(DefaultValue);
  const [childCategories, setChildCategories] = useState([]);
  const [selectedChildCategory, setSelectedChildCategory] =
    useState(DefaultValue);
  const [openedFilters, setOpenedFilters] = useState([]); // 아이콘회전용
  const [searchWord, setSearchWord] = useState('');
  const [searchOptionResults, setSearchOptionResults] = useState([]);

  // 대카테고리 호출
  useEffect(() => {
    (async () => {
      try {
        if (parnetCategories?.length >= 1) return;
        const { data } = await manualApi.getParentCategories();
        setParentCategories(data.categories);
      } catch (error) {
        console.error(error);
      }
    })();
  }, [parnetCategories]);

  // 소카테고리 호출
  useEffect(() => {
    (async () => {
      try {
        if (selectedParentCategory === DefaultValue) return;
        const { data } = await manualApi.getChildCategories(
          selectedParentCategory,
        );
        setSelectedChildCategory(DefaultValue);
        setChildCategories(data.category);
      } catch (error) {
        console.error(error);
      }
    })();
  }, [selectedParentCategory]);

  // 검색어 입력시 실시간 자동완성 결과 가져오기
  useEffect(() => {
    (async () => {
      try {
        if (searchWord?.length < 1) {
          setSearchOptionResults([]);
          reload();
        } else {
          const apiParams = {
            level1: selectedParentCategory,
            level2: selectedChildCategory,
            search: searchWord,
          };
          if (selectedParentCategory === DefaultValue) delete apiParams.level1;
          if (selectedChildCategory === DefaultValue) delete apiParams.level2;
          // my와 total 분기
          let options = [];
          if (location === 'my') {
            const { data } = await manualApi.getMyManuals(apiParams);
            options =
              data?.myBooks?.map(book => ({
                label: book.book.book_title,
                idx: book.book_idx,
              })) || [];
          } else {
            const { data } = await manualApi.getAllManuals(apiParams);
            options =
              data?.books?.map(book => ({
                label: book.book_title,
                idx: book.idx,
              })) || [];
          }
          setSearchOptionResults(options);
        }
      } catch (error) {
        console.error(error);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, selectedParentCategory, selectedChildCategory, searchWord]);

  // 검색필드 이벤트 처리
  const handleSearch = async (event, value) => {
    // 교재 원본 데이터 호출
    let allManuals = [];
    try {
      const apiParams = {
        level1: selectedParentCategory,
        level2: selectedChildCategory,
        search: searchWord,
      };
      if (selectedParentCategory === DefaultValue) delete apiParams.level1;
      if (selectedChildCategory === DefaultValue) delete apiParams.level2;
      // 내교재, 전체교재 분기
      if (location === 'my') {
        const { data } = await manualApi.getMyManuals(apiParams);
        allManuals = data.myBooks;
      } else {
        const { data } = await manualApi.getAllManuals(apiParams);
        allManuals = data.books;
      }
      allManuals?.sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      );
    } catch (error) {
      console.error(error);
    }

    // 이벤트 처리
    if (event.type === 'keydown') {
      if (location === 'my') {
        const targets = allManuals.filter(
          m => m.book.book_title.includes(value) || m.book.idx === value.idx,
        );
        const result = targets?.length < 1 ? [] : targets;
        setManuals(result);
      } else {
        const targets = allManuals.filter(
          m => m.book_title.includes(value) || m.idx === value.idx,
        );
        const result = targets?.length < 1 ? [] : targets;
        setManuals(result);
      }
    } else if (event.type === 'click') {
      if (value === null) {
        resetSearch();
        reload();
      } else {
        console.log('allManuals', allManuals);
        if (location === 'my') {
          const targets = allManuals.find(m => m.book.idx === value.idx);
          const result = targets === undefined ? [] : [targets];
          setManuals(result);
        } else {
          const targets = allManuals.find(m => m.idx === value.idx);
          console.log('targets', targets);
          const result = targets === undefined ? [] : [targets];
          setManuals(result);
        }
      }
    }
  };

  const resetSearch = () => {
    setSearchWord('');
    setSearchOptionResults([]);
  };

  return (
    <div className="flex gap-1 justify-center items-center">
      <div>
        <Select
          value={selectedParentCategory}
          onChange={event => setParentCategory(event.target.value)}
          onOpen={() => setOpenedFilters([...openedFilters, '구분'])}
          onClose={() =>
            setOpenedFilters(openedFilters.filter(f => f !== '구분'))
          }
          displayEmpty
          size="small"
          sx={{ width: '160px', background: '#fff', color: '#A9B6CA' }}
          IconComponent={props => (
            <img
              {...props}
              src={ArrowIc}
              alt="ArrowIcon"
              style={{
                width: '25px',
                height: '25px',
                marginTop: '-3px',
                transform: openedFilters.includes('구분')
                  ? 'rotate(180deg)'
                  : 'rotate(0deg)',
                transition: 'transform 0.3s ease',
                cursor: 'pointer',
              }}
            />
          )}
        >
          <MenuItem value={DefaultValue}>구분</MenuItem>
          {parnetCategories?.length >= 1 &&
            parnetCategories.map(pCate => {
              return (
                <MenuItem key={pCate.idx} value={pCate.idx}>
                  {pCate.cate_name}
                </MenuItem>
              );
            })}
        </Select>
      </div>
      <div>
        <Select
          value={selectedChildCategory}
          onChange={event => setSelectedChildCategory(event.target.value)}
          onOpen={() => setOpenedFilters([...openedFilters, '유형'])}
          onClose={() =>
            setOpenedFilters(openedFilters.filter(f => f !== '유형'))
          }
          displayEmpty
          size="small"
          IconComponent={props => (
            <img
              {...props}
              src={ArrowIc}
              alt="Arrow Icon"
              style={{
                width: '25px',
                height: '25px',
                marginTop: '-3px',
                transform: openedFilters.includes('유형')
                  ? 'rotate(180deg)'
                  : 'rotate(0deg)',
                transition: 'transform 0.3s ease',
                cursor: 'pointer',
              }}
            />
          )}
          sx={{ width: '160px', background: '#fff', color: '#A9B6CA' }}
        >
          <MenuItem value={DefaultValue}>유형</MenuItem>
          {childCategories?.length >= 1 &&
            childCategories.map(cCate => {
              return (
                <MenuItem key={cCate.idx} value={cCate.idx}>
                  {cCate.cate_name}
                </MenuItem>
              );
            })}
        </Select>
      </div>
      <div className="relative">
        <Autocomplete
          disablePortal
          size="small"
          freeSolo
          options={searchOptionResults}
          sx={{ width: '240px', background: '#fff' }}
          renderInput={params => (
            <TextField
              {...params}
              value={searchWord}
              onChange={e => setSearchWord(e.target.value)}
              placeholder="교재 검색"
            />
          )}
          onChange={handleSearch}
        />
        <IconButton
          size="small"
          disableRipple
          sx={{
            display: searchWord?.length >= 1 ? 'none' : 'block',
            position: 'absolute',
            top: '50%',
            right: '25px',
            transform: 'translate(50%, -50%)',
            cursor: 'auto',
          }}
        >
          <img src={SearchIc} alt="SearchIc" />
        </IconButton>
      </div>
      {/* <div className="text-[#383C4B] font-[14px] font-[400]">
              검색결과 : <span className="font-[700]">0권</span>
            </div> */}
    </div>
  );
}

FilterSearch.propTypes = {
  location: PropTypes.string.isRequired,
  reload: PropTypes.func.isRequired,
  setManuals: PropTypes.func.isRequired,
};

export default FilterSearch;
